<script>
  import { onMount, tick } from "svelte";
  import SpotlightGuide from "@components/common/SpotlightGuide.svelte";
  import { registerViewTour } from "lib/stores/viewTourStore.js";
  import QrCode from "svelte-qrcode";
  import { projectListStore } from "@stores/damProjectsStore.js";
  import { recordingStore } from "@stores/recordingStore.js";
  import { useUrlParams } from "@stores/urlStore.js";
  import { PROJECT_STAGES } from "@utils/stagesAndSteps.js";
  import { fly } from "svelte/transition";
  import {
    ToastNotification,
    Modal as DangerModal,
    Tag,
  } from "carbon-components-svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  // import MultiSelect from "lib/components/MultiSelect.svelte";

  import Modal from "../../../../lib/components/Modal.svelte";
  import { formatISOTimestamp } from "../../../../lib/util/formatfunctions";
  import Header from "@components/Header.svelte";
  import DataTableBlack from "@components/DataTableBlack.svelte";
  import ProjectStepActions from "../components/ProjectStepActions.svelte";
  import ImportTeamUsersModal from "../components/ImportTeamUsersModal.svelte";
  import GroupedListTooltip from "../components/GroupedListTooltip.svelte";
  import TeamSubtaskAssignment from "../components/TeamSubtaskAssignment.svelte";
  import LevelChip from "../components/LevelChip.svelte";
  import EmptyStateText from "../components/EmptyStateText.svelte";
  import { downloadProjectTemplate } from "@utils/downloads.js";
  import InstallationInstructions from "../InstallationInstructions.svelte";
  import {
    InfoBlock,
    SaveStatus,
    SectionWrapper,
    TextInput,
    Select,
    SelectItem,
    Button,
    Tooltip,
  } from "paxray-components-svelte";
  import SearchInput from "../../settings/components/SearchInput.svelte";
  import { TRASH_ICON, EDIT_ICON, WARNING_ICON } from "@utils/icons.js";

  export let isCompleted = false;

  const { getProjectLimitations, getProjectName } = projectListStore;
  const { getSubprocesses } = recordingStore;
  const urlParams = useUrlParams();

  const step = PROJECT_STAGES.SETUP.steps.TEAMS;
  const stage = PROJECT_STAGES.SETUP.id;
  let assignmentsLoaded = false;
  let assignmentsSaving = false;
  let assignmentsStorageError = "";
  // Nach dem Lock gibt es nichts mehr zu speichern, dann entfaellt der Speicherstatus im Header
  let teamSettingsCompleted = false;
  $: if (idproject)
    projectListStore
      .isStepCompleted(idproject, step, stage)
      .then((completed) => (teamSettingsCompleted = completed));

  let baseurl = "";
  let installerGenerated = isCompleted;
  let currentTeam = { damUsers: [], subprocesses: [] };
  let teams = [];
  let teamModalOpen = false;
  let teamImportModalOpen = false;
  let users = [];
  let subprocesses = [];
  let qrModalOpen = false;
  let installationInstructionsModalOpen = false;
  let projectName = "Project";
  let importing = false;
  // Imported users that wait for their sensor, see /pending-team-users.
  let pendingUsers = [];
  let pendingReviewOpen = false;
  let selectedPendingIds = [];
  let pendingSensorChoice = {};
  // Limits the review list to one team when opened from the team table.
  let reviewTeamId = null;
  let applyingPending = false;
  let teamsPage = 1;
  let teamsPageSize = 10;
  let usersPage = 1;
  let usersPageSize = 10;
  let teamDataLoaded = false;
  let unassignedSubtaskCount = 0;
  let subtaskAssignmentsReady = false;
  let assignedSubtaskCounts = {};
  let assignedLevelBreakdown = {};
  let usersSortKey = "lastupdate";
  let usersSortDirection = "descending";
  let showTeamGuide = false;
  let teamGuideStep = 0;
  let teamGuideSteps = [];

  let toastMsg;
  let saveSuccessful;

  let openDangerModal;
  let selectedOption = "all";
  let selectedDomain = "all";
  let teamSearchValue = "";
  let usersSearchValue = "";
  // Changes on every opening, so the search field starts empty for the next team.
  let teamModalSession = 0;
  let listOfDomains = [];

  $: ({ idproject } = $urlParams);

  $: if (idproject) {
    assignedSubtaskCounts = {};
    assignedLevelBreakdown = {};
    loadProjectData();
  }

  $: projectName = $projectListStore.currentProjectName || "Project";

  $: {
    baseurl =
      window.location.protocol +
      "//" +
      window.location.host +
      (window.location.port ? ":" + window.location.port : "");
  }

  $: filteredUsers = users.filter((user) => {
    const normalizedSearch = usersSearchValue.trim().toLowerCase();

    let passesAssignmentFilter = true;
    if (selectedOption === "assigned") {
      passesAssignmentFilter = user.assigned !== "";
    } else if (selectedOption === "unassigned") {
      passesAssignmentFilter = user.assigned === "";
    }

    //Filter by domain
    let passesDomainFilter = true;
    if (selectedDomain !== "all") {
      passesDomainFilter = user.domain === selectedDomain;
    }

    let passesSearchFilter = true;
    if (normalizedSearch) {
      passesSearchFilter = [
        user.first_name,
        user.last_name,
        user.name,
        user.email,
        user.domain,
        user.assigned,
      ].some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(normalizedSearch),
      );
    }

    return passesAssignmentFilter && passesDomainFilter && passesSearchFilter;
  });

  $: filteredTeams = teams.filter((team) => {
    const normalizedSearch = teamSearchValue.trim().toLowerCase();
    if (!normalizedSearch) return true;

    return [team.name, String(team.damUsers?.length ?? 0)].some((value) =>
      String(value ?? "")
        .toLowerCase()
        .includes(normalizedSearch),
    );
  });

  $: teamsWithTooFewUsers = teams.filter(
    (team) => (team.damUsers?.length || 0) < 3,
  );
  $: hasTeams = teams.length > 0;
  $: hasTeamWithTooFewUsers = teamsWithTooFewUsers.length > 0;
  $: teamsWithoutSubtasks = teams.filter(
    (team) => assignedSubtaskCounts[String(team.id)] === 0,
  );
  $: teamSetupIssues = [
    hasTeamWithTooFewUsers ? "Some teams have fewer than 3 members" : "",
    teamsWithoutSubtasks.length ? "Some teams have no assigned subtasks" : "",
  ].filter(Boolean);
  $: teamCountsLoaded =
    hasTeams &&
    teams.every((team) => assignedSubtaskCounts[String(team.id)] != null);
  $: if (
    teamsPage >
    Math.max(Math.ceil((filteredTeams || []).length / teamsPageSize), 1)
  ) {
    teamsPage = 1;
  }
  $: if (
    usersPage >
    Math.max(Math.ceil((filteredUsers || []).length / usersPageSize), 1)
  ) {
    usersPage = 1;
  }
  $: pendingToReview = pendingUsers.filter(
    (entry) => entry.candidates.length > 0,
  );
  $: pendingByTeam = pendingUsers.reduce((result, entry) => {
    (result[entry.team_id] ||= []).push(entry);
    return result;
  }, {});
  $: currentTeamPending = currentTeam.id
    ? pendingByTeam[currentTeam.id] || []
    : [];
  $: readyByTeam = pendingToReview.reduce((result, entry) => {
    result[entry.team_id] = (result[entry.team_id] || 0) + 1;
    return result;
  }, {});
  $: movingSelections = selectedPendingIds
    .map((id) =>
      pendingToReview
        .find((entry) => entry.id === id)
        ?.candidates.find(
          (candidate) => candidate.id === pendingSensorChoice[id],
        ),
    )
    .filter((sensor) => sensor?.other_projects.length);
  $: reviewRows = reviewTeamId
    ? pendingToReview.filter((entry) => entry.team_id === reviewTeamId)
    : pendingToReview;

  function editTeam(id) {
    if (id != 0) {
      currentTeam = { ...teams.find((x) => x.id == id) };
    } else {
      currentTeam = {
        id: 0,
        name: "",
        typeIndex: 0,
        damUsers: [],
        subprocesses: [],
      };
    }
    selectedOption = "all";
    selectedDomain = "all";
    usersSearchValue = "";
    usersPage = 1;
    teamModalSession += 1;
    usersSortKey = "lastupdate";
    usersSortDirection = "descending";
    teamModalOpen = true;
  }

  function saveTeam() {
    let method = "POST";
    const teamToSave = {
      name: currentTeam.name,
      idproject: idproject,
      damUsers: currentTeam.damUsers,
      subprocesses: currentTeam.subprocesses,
    };

    if (currentTeam.id) {
      method = "PATCH";
      teamToSave.id = currentTeam.id;
    }

    fetchWithSession(
      `/api/damprojects/dam-teams${currentTeam.id ? "/" + currentTeam.id : ""}`,
      {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(teamToSave),
      },
    )
      .then((res) => res.json())
      .then((data) => {
        saveSuccessful = data.ok;
        if (saveSuccessful) {
          if (data.data.id != currentTeam.id) {
            currentTeam.id = data.data.id;
            teams.push(currentTeam);
            teams = teams;
          } else {
            teams[teams.findIndex((x) => x.id == currentTeam.id)] = currentTeam;
          }
          updateUserAssignments();
        }
        teams = teams;
        teamModalOpen = false;
        toastMsg = data.message || data.error;
      })
      .catch((err) => {
        teamModalOpen = false;
        toastMsg = err.message;
      });
  }

  function deleteTeam() {
    fetchWithSession(`/api/damprojects/dam-teams/${currentTeam.id}`, {
      method: "DELETE",
    })
      .then((res) => res.json())
      .then((data) => {
        saveSuccessful = data.ok;
        if (saveSuccessful) {
          teams = teams.filter((x) => x.id != currentTeam.id);
          updateUserAssignments();
        }
        toastMsg = data.message || data.error;
      });

    openDangerModal = false;
  }

  async function handleLastUpdateClick(row) {
    try {
      const response = await fetchWithSession(`/api/settings/apikey`);
      const data = await response.json();
      const apiKey = data.apikey;
      window.open(
        `/data/getConfig?key=${apiKey}&username=${row.fullname}&version=0&checkuser=true`,
        "_blank",
      );
    } catch (err) {
      console.error("Error getting apikey:", err);
    }
  }

  async function getDamTeams() {
    return fetchWithSession(
      `/api/damprojects/dam-teams?idproject=${idproject}`,
      {
        method: "GET",
      },
    )
      .then((res) => res.json())
      .then((data) => {
        teams = data.map((team) => ({
          ...team,
          damUsers: team.damUsers.map((user) => user.id),
          subprocesses: team.subprocesses.map((subprocess) => subprocess.id),
        }));
      })
      .catch((err) => {
        console.error(err);
      });
  }

  function updateUserAssignments() {
    users = users
      .map((user) => {
        const assignedTeams = teams
          .filter((team) => team.damUsers.includes(user.id))
          .map((team) => team.name);

        return {
          ...user,
          assigned: assignedTeams.join(", ") || "",
        };
      })
      .sort((a, b) => {
        if (!a.assigned && !b.assigned) return 0;
        if (!a.assigned) return 1;
        if (!b.assigned) return -1;
        return a.assigned.localeCompare(b.assigned);
      });
  }

  function displayCellValue(value) {
    const displayValue = String(value ?? "").trim();
    return displayValue || "-";
  }

  async function loadPendingUsers() {
    try {
      const response = await fetchWithSession(
        `/api/damprojects/pending-team-users?idproject=${idproject}`,
      );
      const data = await response.json();
      pendingUsers = data.ok ? data.data : [];
    } catch (err) {
      pendingUsers = [];
      console.error("Error loading imported users:", err);
    }
  }

  function getPendingName(entry) {
    return (
      [entry.first_name, entry.last_name].filter(Boolean).join(" ") ||
      entry.login
    );
  }

  // Hover list for the waiting and assign badges, so it is visible who still has to report
  function pendingTooltip(entries, title) {
    const items = entries.map((entry) => {
      const name = getPendingName(entry);
      return name === entry.login ? name : `${name} (${entry.login})`;
    });
    return [{ title, groups: [{ name: "", items }] }];
  }

  function openPendingReview(teamId = null) {
    reviewTeamId = teamId;
    const entries = teamId
      ? pendingToReview.filter((entry) => entry.team_id === teamId)
      : pendingToReview;
    pendingSensorChoice = Object.fromEntries(
      entries.map((entry) => [
        entry.id,
        entry.suggested_user_id || entry.candidates[0].id,
      ]),
    );
    selectedPendingIds = entries
      .filter((entry) => entry.suggested_user_id)
      .map((entry) => entry.id);
    pendingReviewOpen = true;
  }

  // Carbon clears the selection when the header box is partially checked.
  // Select all rows instead, so one click is enough.
  async function handlePendingSelectAll(event) {
    if (!event.detail.indeterminate) return;
    await tick();
    selectedPendingIds = reviewRows.map((row) => row.id);
  }

  async function handleTeamImportSubmit(event) {
    importing = true;
    try {
      const response = await fetchWithSession(
        `/api/damprojects/pending-team-users/import`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idproject, rows: event.detail.rows || [] }),
          suppressSuccessToast: true,
        },
      );
      const data = await response.json();
      if (!response.ok || !data.ok) {
        throw new Error(data.message || "The import could not be saved.");
      }

      const result = data.data;
      teamImportModalOpen = false;
      await getDamTeams();
      await loadUsers();
      await loadPendingUsers();

      const issues = result.issues
        .slice(0, 3)
        .map((issue) => `Row ${issue.row}: ${issue.message}`)
        .join(" ");
      saveSuccessful = result.skipped === 0;
      toastMsg =
        `${result.added + result.updated} user(s) imported, ${result.teams_created} team(s) created` +
        (result.already_assigned
          ? `, ${result.already_assigned} already in their team`
          : "") +
        (result.skipped ? `, ${result.skipped} skipped. ${issues}` : ".");

      if (pendingToReview.length) openPendingReview();
    } catch (err) {
      saveSuccessful = false;
      toastMsg = err.message;
    } finally {
      importing = false;
    }
  }

  async function applyPendingReview() {
    const assignments = selectedPendingIds.map((id) => ({
      pending_id: id,
      user_id: pendingSensorChoice[id],
    }));
    if (!assignments.length) return;

    if (
      new Set(assignments.map((item) => item.user_id)).size !==
      assignments.length
    ) {
      saveSuccessful = false;
      toastMsg = "A sensor can only be assigned to one user.";
      return;
    }

    applyingPending = true;
    try {
      const response = await fetchWithSession(
        `/api/damprojects/pending-team-users/apply`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idproject, assignments }),
          suppressSuccessToast: true,
        },
      );
      const data = await response.json();
      if (!response.ok || !data.ok) {
        throw new Error(data.message || "The assignments could not be saved.");
      }
      pendingReviewOpen = false;
      await getDamTeams();
      await loadUsers();
      await loadPendingUsers();
      saveSuccessful = true;
      toastMsg = `${data.data.assigned} sensor(s) assigned.`;
    } catch (err) {
      saveSuccessful = false;
      toastMsg = err.message;
    } finally {
      applyingPending = false;
    }
  }

  async function removePendingUser(entry) {
    try {
      const response = await fetchWithSession(
        `/api/damprojects/pending-team-users/${entry.id}`,
        { method: "DELETE", suppressSuccessToast: true },
      );
      const data = await response.json();
      if (!response.ok || !data.ok) {
        throw new Error(data.message || "The user could not be removed.");
      }
      pendingUsers = pendingUsers.filter((item) => item.id !== entry.id);
    } catch (err) {
      saveSuccessful = false;
      toastMsg = err.message;
    }
  }

  async function loadProjectData() {
    teamDataLoaded = false;
    getProjectName(idproject);
    await getDamTeams();
    await loadUsers();
    await loadPendingUsers();
    await getSubprocesses();
    await loadProjectSubprocesses();
    await getProjectLimitations(idproject, stage);
    teamDataLoaded = true;
  }

  async function loadProjectSubprocesses() {
    try {
      const [projectResponse, departmentsResponse] = await Promise.all([
        fetchWithSession(`/api/damprojects/projects/${idproject}`),
        fetchWithSession("/api/damprojects/departments"),
      ]);
      const [project, departments] = await Promise.all([
        projectResponse.json(),
        departmentsResponse.json(),
      ]);
      const projectDepartmentIds = new Set(
        (project.departments || []).map((department) => department.id),
      );
      const projectProcessIds = new Set(
        (departments || [])
          .filter((department) => projectDepartmentIds.has(department.id))
          .flatMap((department) => department.processes || [])
          .map((process) => process.id),
      );

      subprocesses = ($recordingStore.subprocesses || []).filter((subprocess) =>
        projectProcessIds.has(subprocess.process_id),
      );
    } catch (err) {
      subprocesses = [];
      console.error("Error loading project subprocesses:", err);
    }
  }

  function loadUsers() {
    return fetchWithSession(`/api/damprojects/getDAMTeamUsers`)
      .then((res) => res.json())
      .then((data) => {
        const dataTimeFormated = data.map((item) => ({
          ...item,
          lastupdate: item.lastupdate
            ? formatISOTimestamp(item.lastupdate.slice(0, -6))
            : "-",
        }));
        const tempDomains = [];
        users = dataTimeFormated
          .map((user) => {
            const rawName = user.name || "";
            const [domain, ...nameParts] = rawName.split("\\");
            const name = nameParts.join("\\").replace(/^\\+/, "");
            if (domain) tempDomains.push(domain);
            const assignedTeam = teams
              .filter((team) => team.damUsers.includes(user.id))
              .map((team) => team.name);
            return {
              ...user,
              domain: domain || "-",
              first_name: user.first_name || user.firstName || "-",
              last_name: user.last_name || user.lastName || "-",
              name: name || rawName || "-",
              email: user.email || "-",
              assigned: assignedTeam.join(", ") || "",
              fullname: rawName,
            };
          })
          .sort((a, b) => {
            if (!a.assigned && !b.assigned) return 0;
            if (!a.assigned) return 1;
            if (!b.assigned) return -1;
            return a.assigned.localeCompare(b.assigned);
          });
        listOfDomains = [...new Set(tempDomains)];
      })
      .catch((err) => {
        console.error(err);
      });
  }

  onMount(() =>
    registerViewTour(() => {
      teamGuideSteps = [
        {
          target: '[data-guide="create-team"] button',
          title: "Create a new team",
          text: "Create a team and give it a clear name. In the team dialog, select at least three members. Use search and filters to find the people you need, then save your team.",
        },
        {
          target: '[data-guide="import-teams"] button',
          title: "Import teams from Excel",
          text: "Upload the project template to create all teams and add their users at once. Imported users wait in their team until their sensor reports for the first time.",
        },
        {
          target: '[data-guide="team-overview"] table',
          title: "Your project teams",
          text: "See your project teams, their member counts and assigned subtasks. Red badges indicate teams with one or two members or no assigned subtasks. Imported users without a sensor are shown as waiting. Use the search above the table to find a team by name.",
        },
        {
          target: '[data-guide="team-overview"] .assign-tag',
          title: "Assign registered sensors",
          text: "As soon as the sensor of an imported user reports, the team shows an Assign badge. Click it to check the match and add the sensor to the team. Sensors that currently belong to another project are marked in red.",
        },
        {
          target: '[data-guide="pending-review"] button',
          title: "Review all matches",
          text: "Open all matches of every team at once. Clear matches are already selected, so you can add them with one click.",
        },
        {
          target:
            '[data-guide="team-overview"] .table-actions > button:first-of-type',
          title: "Review and edit teams",
          text: "Use the pencil icon to open an existing team and review or update its member assignments.",
        },
        {
          target:
            '[data-guide="team-overview"] .table-actions > button:nth-of-type(2)',
          title: "Delete a team",
          text: "Use the trash icon to remove a team you no longer need. You will be asked to confirm before the team is deleted.",
        },
        ...(hasTeams && subtaskAssignmentsReady
          ? [
              {
                target:
                  '[data-guide="team-settings"] .team-subtask-assignment table',
                title: "Assign subtasks to teams",
                text: "Assign the subtasks selected in your project framework to the responsible teams, individually or for an entire process. Every subtask needs at least one team. Any subtasks without a team are highlighted in red.",
              },
            ]
          : []),
        {
          target:
            '[data-guide="complete-team-settings"] > button, [data-guide="complete-team-settings"] > .completed-badge',
          title: "Complete and lock team settings",
          text: 'Review all teams and assignments before completing setup. Each team needs at least three members, and every selected subtask must have a team. Completing this step locks the settings and cannot be undone. Once this step is complete, the button is replaced by "Completed".',
        },
      ].filter(({ target }) => {
        const element = document.querySelector(target);
        return (
          element &&
          getComputedStyle(element).visibility === "visible" &&
          element.getBoundingClientRect().width > 0 &&
          element.getBoundingClientRect().height > 0
        );
      });
      teamGuideStep = 0;
      showTeamGuide = teamGuideSteps.length > 0;
    }),
  );

  $$restProps;
</script>

<Header
  section="Project"
  area="Setup"
  breadcrumb="Set Up Teams"
  tooltipText="Create project teams and assign users and project subtasks for data collection."
>
  <!-- Ohne Teams gibt es keine Zuweisungen und damit keinen Speicherstand -->
  {#if assignmentsLoaded && teams.length && !assignmentsStorageError && !teamSettingsCompleted}
    <SaveStatus saving={assignmentsSaving} />
  {/if}
  <div data-guide="create-team">
    <Button
      kind="secondary"
      size="field"
      text="Create New Team"
      on:click={() => editTeam(0)}
    />
  </div>
  <div data-guide="import-teams">
    <Button
      kind="secondary"
      size="field"
      disabled={installerGenerated}
      text="Import Teams (xlsx)"
      on:click={() => {
        teamImportModalOpen = true;
      }}
    />
  </div>
  <div data-guide="complete-team-settings">
    <ProjectStepActions
      {idproject}
      {step}
      {stage}
      showGoBackButton={false}
      bind:toastMsg
      bind:saveSuccessful
      allowAdminOverride={true}
      disabled={hasTeamWithTooFewUsers ||
        !subtaskAssignmentsReady ||
        unassignedSubtaskCount > 0}
      confirmBeforeComplete={true}
      text="Complete & Lock Team Settings"
      size="sm"
      completionWarning="After completion, the team settings cannot be changed. Please make sure all teams and assignments are correct before continuing. This action cannot be undone."
    />
  </div>
</Header>
<!-- Ohne Teams statt leerer Tabellen der zentrale Leerzustand mit beiden Wegen, Teams anzulegen -->
<SectionWrapper
  fullscreen={true}
  seamless={true}
  seamlessPadding={true}
  loading={!teamDataLoaded}
  loadingTitle="Loading teams."
  loadingHint="Please wait while the project teams are prepared."
  emptyState={teamDataLoaded && !hasTeams}
>
  <div slot="emptyState">
    <EmptyStateText
      title="No teams yet"
      hint="Create a team or import your teams from an Excel file. The project template shows the expected format."
    >
      <svelte:fragment slot="actions">
        <Button
          kind="secondary"
          size="field"
          text="Create New Team"
          on:click={() => editTeam(0)}
        />
        <Button
          kind="secondary"
          size="field"
          disabled={installerGenerated}
          text="Import Teams (xlsx)"
          on:click={() => {
            teamImportModalOpen = true;
          }}
        />
        <Button
          kind="outline-dark"
          size="field"
          text="Download Template"
          on:click={downloadProjectTemplate}
        />
      </svelte:fragment>
    </EmptyStateText>
  </div>
  <div class="main" slot="main" data-guide="team-settings">
    <div class="team-overview" data-guide="team-overview">
      {#if teamDataLoaded}
        {#if hasTeams}
          <InfoBlock
            type={teamSetupIssues.length
              ? "warning"
              : teamCountsLoaded
                ? "hint"
                : "info"}
            heading={teamSetupIssues.length
              ? teamSetupIssues.join(" • ")
              : teamCountsLoaded
                ? "All teams have at least 3 members and assigned subtasks."
                : "Checking team assignments…"}
            message={teamSetupIssues.length
              ? "Look for the red chips in the table below."
              : teamCountsLoaded
                ? "Review the subtask assignments below before completing setup."
                : "Subtask counts are loading."}
            showClose={false}
          />
        {/if}
        {#if pendingToReview.length}
          <InfoBlock
            type="info"
            heading="{pendingToReview.length} imported user(s) can be matched to a sensor"
            message="Check the suggested sensors and add them to their teams."
            showClose={false}
            showActions
          >
            <div slot="actions" data-guide="pending-review">
              <Button
                kind="secondary"
                size="field"
                text="Review"
                on:click={() => openPendingReview()}
              />
            </div>
          </InfoBlock>
        {/if}
      {/if}
      <DataTableBlack
        headers={[
          {
            key: "name",
            value: "Team",
            tooltip: "Name of the team used to group users for this project.",
          },
          {
            key: "users",
            value: "Members",
            tooltip: "Number of members currently assigned to this team.",
          },
          {
            key: "coverage",
            value: "Assignments",
            sort: false,
            tooltip:
              "Subtasks (L4) assigned to this team and the processes (L1), subprocesses (L2) and tasks (L3) they belong to.\nHover over a chip to see the list.",
          },
          { key: "menu", value: "Actions", width: "101px" },
        ]}
        rows={(filteredTeams || []).map((team) => ({
          ...team,
          coverage: assignedLevelBreakdown[String(team.id)] ?? null,
        }))}
        totalItems={(filteredTeams || []).length}
        searchValue={teamSearchValue}
        showNewButton={false}
        showToolbar
        slicePage={true}
        bind:page={teamsPage}
        bind:pageSize={teamsPageSize}
        sortable
        on:search={(e) => {
          teamSearchValue = e.detail.value;
          teamsPage = 1;
        }}
        on:clearSearch={() => {
          teamSearchValue = "";
          teamsPage = 1;
        }}
      >
        <svelte:fragment slot="cell-header" let:header>
          {#if header.tooltip}
            <div class="header_with_tooltip">
              {header.value}
              <Tooltip>{header.tooltip}</Tooltip>
            </div>
          {:else}
            {header.value}
          {/if}
        </svelte:fragment>
        <svelte:fragment slot="cell" let:cell let:row>
          {#if cell.key === "menu"}
            <div class="table-actions">
              <Button
                icon={EDIT_ICON}
                size="small"
                iconDescription="Edit"
                kind="secondary"
                on:click={() => editTeam(row.id)}
              />
              {#if !installerGenerated}
                <Button
                  icon={TRASH_ICON}
                  size="small"
                  iconDescription="Delete"
                  kind="secondary"
                  on:click={() => {
                    currentTeam = row;
                    openDangerModal = true;
                  }}
                />
              {/if}
            </div>
          {:else if cell.key === "users"}
            {#if row.damUsers.length < 3}
              <Tag type="red" title="At least 3 members required"
                >{row.damUsers.length}</Tag
              >
            {:else}
              <Tag
                type="gray"
                style="background-color: var(--pax-smoke); color: var(--pax-black);"
                >{row.damUsers.length}</Tag
              >
            {/if}
            {#if pendingByTeam[row.id]?.length}
              <GroupedListTooltip
                sections={pendingTooltip(
                  pendingByTeam[row.id],
                  "Imported users without an assigned sensor",
                )}
              >
                <Tag type="outline"
                  >{pendingByTeam[row.id].length} waiting</Tag
                >
              </GroupedListTooltip>
            {/if}
            {#if readyByTeam[row.id]}
              <GroupedListTooltip
                sections={pendingTooltip(
                  pendingToReview.filter((entry) => entry.team_id === row.id),
                  "Sensor reported, click to assign",
                )}
              >
                <Tag
                  type="outline"
                  interactive
                  class="assign-tag"
                  on:click={() => openPendingReview(row.id)}
                  >Assign {readyByTeam[row.id]}</Tag
                >
              </GroupedListTooltip>
            {/if}
          {:else if cell.key === "coverage"}
            {#if cell.value}
              <div class="coverage-chips">
                <LevelChip
                  label="L1"
                  title="Processes"
                  breakdown={cell.value.L1}
                />
                <LevelChip
                  label="L2"
                  title="Subprocesses"
                  breakdown={cell.value.L2}
                />
                <LevelChip label="L3" title="Tasks" breakdown={cell.value.L3} />
                <LevelChip
                  label="L4"
                  title="Subtasks"
                  breakdown={cell.value.L4}
                  warnWhenEmpty
                />
              </div>
            {:else}
              —
            {/if}
          {:else}
            {cell.value}
          {/if}
        </svelte:fragment>
      </DataTableBlack>
    </div>
    {#if teamDataLoaded && idproject}
      {#key idproject}<TeamSubtaskAssignment
          projectId={idproject}
          {teams}
          bind:unassignedCount={unassignedSubtaskCount}
          bind:assignmentsReady={subtaskAssignmentsReady}
          bind:assignedSubtaskCounts
          bind:assignedLevelBreakdown
          bind:loaded={assignmentsLoaded}
          bind:saving={assignmentsSaving}
          bind:storageError={assignmentsStorageError}
        />{/key}
    {/if}
  </div>
</SectionWrapper>

<SpotlightGuide
  bind:open={showTeamGuide}
  bind:currentStep={teamGuideStep}
  steps={teamGuideSteps}
  allowInteraction={false}
/>

{#if toastMsg}
  <div
    in:fly={{ x: 100 }}
    out:fly={{ y: 100 }}
    style="position:fixed; top:60px; right:20px; z-index:100;"
  >
    <ToastNotification
      fullWidth
      hideAfter={saveSuccessful ? 3000 : null}
      timeout={saveSuccessful ? 3000 : null}
      kind={saveSuccessful ? "success" : "error"}
      title={saveSuccessful ? "Success" : "Error"}
      subtitle={toastMsg}
      caption={new Date().toLocaleString()}
      on:close={(e) => {
        e.preventDefault();
        toastMsg = null;
        saveSuccessful = false;
      }}
    />
  </div>
{/if}

<Modal
  autowidth
  bind:open={qrModalOpen}
  preventCloseOnClickOutside
  actions
  modalHeading="Workshop QR Code"
  primaryButtonText="Ok"
  secondaryButtonText="Cancel"
  on:click:button--secondary={() => {
    qrModalOpen = false;
  }}
  primaryButtonAction={() => {
    qrModalOpen = false;
  }}
  on:close={() => {
    qrModalOpen = false;
  }}
>
  <div style="margin:0 auto; width:300px;">
    <QrCode value={baseurl + "/UserInput?idproject=" + idproject} />
    <h4>{baseurl + "/UserInput?idproject=" + idproject}</h4>
  </div>
</Modal>

<div class="scoping-team-modal">
  <Modal
    autowidth
    hasScrollingContent={false}
    modalClass="team-settings-users-modal"
    preventCloseOnClickOutside
    bind:open={teamModalOpen}
    modalHeading="Team {currentTeam.name || ''}"
    on:open
    on:close={() => {
      teamModalOpen = false;
    }}
  >
    <div class="new_team_modal_wrapper">
      <div class="team-name-input">
        <TextInput
          labelText="Team name"
          placeholder="Enter team name"
          bind:value={currentTeam.name}
          readonly={installerGenerated}
          invalid={!currentTeam.name}
          invalidText="Please insert a name for the team"
        />
      </div>

      <div class="select_wrapper">
        <Select labelText="Assignment" bind:selected={selectedOption}>
          <SelectItem value="all" text="Show all" />
          <SelectItem value="assigned" text="Show assigned users" />
          <SelectItem value="unassigned" text="Show unassigned users" />
        </Select>

        <Select labelText="Domain" bind:selected={selectedDomain}>
          <SelectItem value="all" text="All domains" />
          {#each listOfDomains as domain}
            <SelectItem value={domain} text={domain} />
          {/each}
        </Select>

        <div class="user-search-control">
          <span class="user-filter-label">Search</span>
          {#key teamModalSession}
            <SearchInput
              placeholder="Search users..."
              on:search={(event) => {
                usersSearchValue = event.detail;
                usersPage = 1;
              }}
            />
          {/key}
        </div>
      </div>
      <div class="team-users-table">
        <DataTableBlack
          size="short"
          headers={[
            { key: "first_name", value: "First Name" },
            { key: "last_name", value: "Last Name" },
            { key: "name", value: "System Login Name" },
            { key: "email", value: "E-Mail" },
            { key: "domain", value: "Domain" },
            { key: "lastupdate", value: "Last Updated" },
            { key: "assigned", value: "Assigned team" },
          ]}
          rows={filteredUsers || []}
          totalItems={(filteredUsers || []).length}
          showToolbar={false}
          showNewButton={false}
          slicePage={true}
          pageSizes={[5, 10, 25, 50, 75, 100]}
          bind:page={usersPage}
          bind:pageSize={usersPageSize}
          bind:sortKey={usersSortKey}
          bind:sortDirection={usersSortDirection}
          bind:selectedRowIds={currentTeam.damUsers}
          sortable
          batchSelection
        >
          <svelte:fragment slot="cell-header" let:header>
            {#if header.key === "lastupdate"}
              <span class="last-update-header">{header.value}</span>
            {:else}
              {header.value}
            {/if}
          </svelte:fragment>
          <svelte:fragment slot="cell" let:cell let:row>
            {#if cell.key === "lastupdate"}
              <div class="last-update-cell">
                <button
                  type="button"
                  class="last-update-button"
                  on:click={() => handleLastUpdateClick(row)}
                >
                  {displayCellValue(cell.value)}
                </button>
              </div>
            {:else}
              {displayCellValue(cell.value)}
            {/if}
          </svelte:fragment>
        </DataTableBlack>
      </div>
      {#if currentTeamPending.length}
        <div class="pending-users">
          <div class="pending-users-heading">
            <span>Imported users without a sensor</span>
            <Tag type="outline">{currentTeamPending.length}</Tag>
            <Tooltip
              >These users were imported from Excel. They are added to the
              team as soon as their sensor registers and you confirm the
              match.</Tooltip
            >
          </div>
          <DataTableBlack
            size="short"
            headers={[
              { key: "first_name", value: "First Name" },
              { key: "last_name", value: "Last Name" },
              { key: "login", value: "System Login Name" },
              { key: "email", value: "E-Mail" },
              { key: "status", value: "Status" },
              ...(installerGenerated
                ? []
                : [{ key: "menu", value: "", width: "60px" }]),
            ]}
            rows={currentTeamPending}
            showToolbar={false}
            showPagination={false}
            sortable={false}
          >
            <svelte:fragment slot="cell" let:cell let:row>
              {#if cell.key === "status"}
                <Tag type="outline">Waiting for sensor</Tag>
              {:else if cell.key === "menu"}
                <div class="table-actions">
                  <Button
                    icon={TRASH_ICON}
                    size="small"
                    iconDescription="Remove"
                    kind="secondary"
                    on:click={() => removePendingUser(row)}
                  />
                </div>
              {:else}
                {displayCellValue(cell.value)}
              {/if}
            </svelte:fragment>
          </DataTableBlack>
        </div>
      {/if}
      <!-- Legacy subprocess selection hidden in preparation for direct L4 assignments.
      <div class="subprocess-selector">
        <MultiSelect
          labelText="Subprocesses"
          label="Select subprocesses ..."
          direction="top"
          items={subprocesses.map((subprocess) => ({
            id: subprocess.id,
            text: subprocess.name,
          }))}
          bind:selectedIds={currentTeam.subprocesses}
        />
      </div>
      -->
    </div>
    <div class="team-modal-divider divider-margin" />
    <div class="actions-buttons-container">
      <Button
        kind="outline-dark"
        text="Cancel"
        on:click={() => {
          teamModalOpen = false;
        }}
      />
      <div class="primary-buttons">
        <Button
          kind="secondary"
          text="Help"
          on:click={() => {
            installationInstructionsModalOpen = true;
          }}
        />
        <Button
          text="Save"
          disabled={!currentTeam.name}
          on:click={() => {
            if (currentTeam.name) saveTeam();
          }}
        />
      </div>
    </div>
  </Modal>
</div>

<Modal
  autowidth
  hideFooter
  modalClass="installation-instructions-modal"
  modalHeading="Installation Instructions"
  bind:open={installationInstructionsModalOpen}
  on:close={() => {
    installationInstructionsModalOpen = false;
  }}
>
  <InstallationInstructions />
</Modal>

<ImportTeamUsersModal
  bind:open={teamImportModalOpen}
  loading={importing}
  {projectName}
  on:submit={handleTeamImportSubmit}
  on:close={() => {
    teamImportModalOpen = false;
  }}
/>

<Modal
  autowidth
  hideFooter
  modalClass="team-import-conflict-modal"
  preventCloseOnClickOutside
  bind:open={pendingReviewOpen}
  modalHeading="Match imported users"
  on:close={() => {
    pendingReviewOpen = false;
  }}
>
  <div class="import-conflict-wrapper">
    <p class="review-intro">
      Selected sensors are added to the imported team together with name and
      e-mail. Clear matches are already selected.
    </p>

    <div class="conflict-table-wrapper">
      <DataTableBlack
        headers={[
          { key: "user", value: "Imported user" },
          { key: "team_name", value: "Team" },
          { key: "sensor", value: "Sensor" },
          { key: "lastupdate", value: "Last Updated" },
        ]}
        rows={reviewRows}
        batchSelection
        bind:selectedRowIds={selectedPendingIds}
        on:click:header--select={handlePendingSelectAll}
        showToolbar={false}
        showPagination={false}
        sortable={false}
      >
        <svelte:fragment slot="cell" let:cell let:row>
          {@const sensor = row.candidates.find(
            (candidate) => candidate.id === pendingSensorChoice[row.id],
          )}
          {#if cell.key === "user"}
            {getPendingName(row)}
          {:else if cell.key === "sensor"}
            <div class="review-sensor">
              {#if row.candidates.length > 1}
                <Select
                  size="sm"
                  hideLabel
                  labelText="Sensor"
                  bind:selected={pendingSensorChoice[row.id]}
                >
                  {#each row.candidates as candidate (candidate.id)}
                    <SelectItem value={candidate.id} text={candidate.name} />
                  {/each}
                </Select>
                <Tag type="outline">{row.candidates.length} matches</Tag>
              {:else}
                <span>{sensor?.name}</span>
              {/if}
              {#if sensor?.other_projects.length}
                <Tag
                  type="red"
                  class="move-tag"
                  title="This sensor is removed from {sensor.other_projects.join(
                    ', ',
                  )} and records for this project afterwards"
                >
                  <span class="move-tag-content">
                    <span
                      class="move-tag-icon"
                      style="--icon: url({WARNING_ICON})"
                      aria-hidden="true"
                    />
                    Moves from project {sensor.other_projects.join(", ")}
                  </span>
                </Tag>
              {:else if sensor?.current_teams.length}
                <Tag type="outline"
                  >Now in {sensor.current_teams.join(", ")}</Tag
                >
              {/if}
            </div>
          {:else if cell.key === "lastupdate"}
            {sensor?.lastupdate
              ? formatISOTimestamp(sensor.lastupdate.slice(0, 19))
              : "-"}
          {:else}
            {displayCellValue(cell.value)}
          {/if}
        </svelte:fragment>
      </DataTableBlack>
    </div>

    {#if movingSelections.length}
      <InfoBlock
        type="warning"
        heading="{movingSelections.length} selected sensor(s) belong to another project"
        message="They are removed from {[
          ...new Set(movingSelections.flatMap((sensor) => sensor.other_projects)),
        ].join(', ')} and record for this project afterwards."
        showClose={false}
      />
    {/if}

    <div class="actions-buttons-container conflict-actions">
      <Button
        kind="secondary"
        text="Later"
        on:click={() => {
          pendingReviewOpen = false;
        }}
      />
      <Button
        kind="secondary"
        text={selectedPendingIds.length
          ? `Add ${selectedPendingIds.length} to teams`
          : "Add to teams"}
        disabled={applyingPending || !selectedPendingIds.length}
        on:click={applyPendingReview}
      />
    </div>
  </div>
</Modal>

<DangerModal
  danger
  bind:open={openDangerModal}
  preventCloseOnClickOutside
  modalHeading="Delete"
  primaryButtonText="Delete"
  secondaryButtonText="Cancel"
  on:click:button--primary={deleteTeam}
  on:click:button--secondary={() => (openDangerModal = false)}
>
  <p class="deleteModalBody">
    Are you sure you want to delete this team:
    <span class="team-name">{currentTeam?.name}</span>?
    <span>This action cannot be undone.</span>
  </p>
</DangerModal>

<style>
  .main {
    max-width: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .team-overview {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .main > div :global(.warning-notice) {
    margin-bottom: 0;
  }

  .coverage-chips {
    display: inline-flex;
    gap: 0.25rem;
    white-space: nowrap;
  }

  .header_with_tooltip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }

  .table-actions {
    display: inline-flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: flex-end;
    gap: 0.4rem;
    white-space: nowrap;
  }

  .table-actions :global(.button_wrapper) {
    flex: 0 0 auto;
  }

  .new_team_modal_wrapper {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .team-users-table {
    margin-top: 1rem;
  }

  /* Bei vielen Zeilen pro Seite scrollt nur die Tabelle, Filter, Pagination
     und Buttons bleiben im Fenster sichtbar. */
  .team-users-table :global(.bx--data-table-container) {
    max-height: max(12rem, calc(100vh - 42rem));
    overflow-y: auto;
  }

  .team-users-table :global(thead th) {
    position: sticky;
    top: 0;
    z-index: 1;
  }

  .select_wrapper {
    margin-top: 1rem;
  }

  :global(.team-name-input .bx--form-item) {
    width: 100%;
    margin-bottom: 0;
  }

  :global(.team-name-input .bx--label) {
    color: var(--pax-black);
  }

  :global(.team-name-input .bx--text-input) {
    height: 3rem;
    min-height: 3rem;
    padding: 0 1rem;
    border: 1px solid var(--pax-black);
    border-radius: 0.5rem;
    background-color: var(--pax-white) !important;
    color: var(--pax-black);
  }

  :global(.team-name-input .bx--text-input:focus) {
    outline: 1px solid var(--pax-black);
    outline-offset: 0;
  }

  .team-name {
    font-weight: bold;
  }

  .select_wrapper {
    display: grid;
    grid-template-columns: repeat(2, minmax(10rem, 1fr)) minmax(14rem, 2fr);
    gap: 1rem;
    align-items: end;
    width: 100%;
  }

  .select_wrapper :global(.bx--form-item),
  .select_wrapper :global(.bx--select),
  .select_wrapper :global(.bx--select-input__wrapper),
  .select_wrapper :global(.bx--select-input) {
    width: 100%;
  }

  .user-search-control {
    min-width: 0;
  }

  .user-filter-label {
    display: block;
    margin-bottom: 0.5rem;
    color: var(--pax-black);
    font-size: 0.75rem;
    line-height: 1rem;
  }

  .user-search-control :global(.search-input) {
    box-sizing: border-box;
    width: 100%;
    height: 2.5rem;
  }

  .user-search-control :global(.search-input input) {
    width: 100%;
    min-width: 0;
  }

  :global(.subprocess-selector .bx--form-item) {
    margin-bottom: 0;
  }

  .team-modal-divider {
    width: 100%;
    height: 1px;
    background: #d9d9d9;
    margin-bottom: 1rem;
  }

  .last-update-header,
  .last-update-cell {
    display: flex;
    justify-content: center;
  }

  .last-update-button {
    border: none;
    background: none;
    padding: 0;
    cursor: pointer;
    color: inherit;
    font: inherit;
    text-align: center;
    text-decoration: underline;
  }

  .divider-margin {
    margin-top: 1rem;
  }

  .actions-buttons-container {
    display: flex;
    justify-content: space-between;
  }

  .actions-buttons-container .primary-buttons {
    display: flex;
    gap: 0.5rem;
  }

  .import-conflict-wrapper {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    max-width: calc(96vw - 4rem);
  }

  .conflict-actions {
    gap: 0.5rem;
    justify-content: flex-end;
  }

  .conflict-table-wrapper {
    min-width: 56rem;
    max-height: 24rem;
    overflow: auto;
  }

  :global(.bx--tag.assign-tag) {
    cursor: pointer;
    transition:
      background-color 0.15s,
      color 0.15s;
  }

  :global(.bx--tag--interactive.bx--tag--outline.assign-tag:hover),
  :global(.bx--tag--interactive.bx--tag--outline.assign-tag:focus-visible) {
    background-color: var(--pax-black);
    color: var(--pax-white);
  }

  :global(.bx--tag.move-tag),
  :global(.bx--tag.move-tag > span) {
    display: inline-flex;
    align-items: center;
  }

  .move-tag-content {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    font-weight: 600;
    line-height: 1;
  }

  /* The icon is used as a mask so it takes the red text color of the tag. */
  .move-tag-icon {
    flex: 0 0 auto;
    width: 0.875rem;
    height: 0.875rem;
    background-color: currentColor;
    -webkit-mask: var(--icon) center / contain no-repeat;
    mask: var(--icon) center / contain no-repeat;
  }

  .review-intro {
    margin: 0;
    font-size: 0.875rem;
    color: #525252;
  }

  .review-sensor {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
  }

  .review-sensor :global(.bx--form-item) {
    flex: 0 1 14rem;
    margin: 0;
  }

  .review-sensor :global(.bx--tag),
  .pending-users-heading :global(.bx--tag) {
    margin: 0;
  }

  .pending-users {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 1.5rem;
  }

  .pending-users-heading {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--pax-black);
  }

  :global(
      .custom-table.zebra-rows .bx--data-table tbody tr:nth-child(even) td
    ) {
    background-color: #f4f4f4 !important;
  }

  :global(.custom-table.zebra-rows .bx--data-table tbody tr:nth-child(odd) td) {
    background-color: #ffffff !important;
  }

  :global(.scoping-team-modal .modal_wrapper .divider),
  :global(.scoping-team-modal .actions-buttons-container) {
    display: none;
  }

  :global(.team-settings-users-modal .bx--modal-container) {
    width: auto !important;
    max-width: 96vw !important;
  }

  :global(.team-settings-users-modal .modal_wrapper) {
    width: 100% !important;
    max-width: 100%;
    min-width: 0;
  }

  :global(.team-settings-users-modal .new_team_modal_wrapper) {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow-x: hidden;
  }

  :global(.team-settings-users-modal .bx--modal-scroll-content) {
    min-width: 0;
    overflow-x: hidden !important;
  }

  :global(.team-settings-users-modal .bx--modal-content) {
    overflow-y: auto !important;
  }

  :global(.team-settings-users-modal .bx--modal-scroll-content > *:last-child) {
    padding-bottom: 0 !important;
  }

  /* Anchored at the top, so the move warning only extends the dialog downwards instead of re-centering it */
  :global(.bx--modal.team-import-conflict-modal) {
    align-items: flex-start;
  }

  :global(.team-import-conflict-modal .bx--modal-container) {
    margin-top: 15vh;
    width: auto !important;
    max-width: 96vw !important;
  }

  :global(.team-import-conflict-modal .modal_wrapper) {
    width: auto !important;
    max-width: calc(96vw - 2rem);
  }

  :global(.installation-instructions-modal .bx--modal-scroll-content) {
    overflow-x: hidden;
  }
</style>
